import { ChangeDetectionStrategy, Component, HostListener, input, model, output } from '@angular/core';

export type DialogSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | '8xl'
  | '9xl'
  | 'full';

@Component({
  selector: 'app-dialog',
  template: `
    @if (visible()) {
      <div
        class="fixed inset-0 z-9998 animate-fade-in"
        (click)="onBackdropClick($event)"
        aria-hidden="true"
      >
        <div class="fixed inset-0 bg-black/30 backdrop-blur-sm"></div>
      </div>
      <div
        class="pointer-events-none fixed inset-0 z-9999 flex animate-slide-up items-start justify-center p-3 sm:items-center sm:p-4 sm:pt-[5vh]"
        role="dialog"
        [attr.aria-modal]="true"
        [attr.aria-label]="header() || null"
      >
        <div
          [class]="panelClasses()"
          (click)="$event.stopPropagation()"
        >
          @if (header() || closable()) {
            <div
              class="flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-6 sm:py-4"
            >
              <h2 class="min-w-0 truncate text-base font-semibold text-slate-800 sm:text-lg">
                {{ header() }}
              </h2>
              @if (closable()) {
                <button
                  class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/30"
                  (click)="close()"
                  aria-label="Cerrar diálogo"
                >
                  <i class="bi bi-x-lg text-lg" aria-hidden="true"></i>
                </button>
              }
            </div>
          }

          <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
            <ng-content />
          </div>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiDialog {
  readonly header = input<string>('');
  readonly modal = input<boolean>(true);
  readonly visible = model<boolean>(false);
  readonly closable = input<boolean>(true);
  readonly closeOnEscape = input<boolean>(true);
  readonly size = input<DialogSize>('sm');

  readonly styleClass = input<string>('');

  readonly onHide = output<void>();

  protected panelClasses(): string {
    const base =
      'pointer-events-auto flex w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5 sm:rounded-2xl max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh]';
    return `${base} ${this.sizeClasses()} ${this.styleClass()}`.trim();
  }

  protected sizeClasses(): string {
    // Si el consumidor define su propio max-width (por ejemplo con styleClass),
    // no aplicamos el del tamaño para evitar conflictos de cascada.
    const hasWidthOverride = this.styleClass()
      .split(/\s+/)
      .some((token) => /(^|:)max-w-/.test(token));

    if (hasWidthOverride) return '';

    const sizes: Record<DialogSize, string> = {
      xs: 'max-w-xs', // 320px
      sm: 'max-w-sm', // 384px
      md: 'max-w-md', // 448px
      lg: 'max-w-lg', // 512px
      xl: 'max-w-xl', // 576px
      '2xl': 'max-w-2xl', // 672px
      '3xl': 'max-w-3xl', // 768px
      '4xl': 'max-w-4xl', // 896px
      '5xl': 'max-w-5xl', // 1024px
      '6xl': 'max-w-6xl', // 1152px
      '7xl': 'max-w-7xl', // 1280px
      '8xl': 'max-w-[88rem]', // 1408px
      '9xl': 'max-w-[96rem]', // 1536px
      full: 'max-w-[calc(100vw-2rem)]',
    };

    return sizes[this.size()] ?? sizes.sm;
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (this.modal() && event.target === event.currentTarget) this.close();
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: KeyboardEvent): void {
    if (this.visible() && this.closeOnEscape()) {
      event.preventDefault();
      this.close();
    }
  }

  close(): void {
    this.visible.set(false);
    this.onHide.emit();
  }
}
